import { getAddress, isAddress, parseUnits } from 'viem';
import type { AbiParameter } from 'viem';
import {
  HighLevelSolidityType,
  ParameterParsedValue,
} from '@mls-wallet/shared-types';
import {
  BytesValidationState,
  NumberValidationState,
  StringValidationState,
  TransactionBuilderParameterState,
  NumberValueType,
} from './types';

/**
 * Get the new parameter state for an address
 *
 * Used for maintaining the state of an address parameter in the transaction builder
 *
 * @param {string} address The address to get the new parameter state for
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForAddress(
  address: string
): TransactionBuilderParameterState {
  const valid = isAddress(address);
  const checksumAddress = valid ? getAddress(address) : undefined;

  const parameterState: TransactionBuilderParameterState = {
    isValid: valid,
    isEmpty: !address || address === '',
    value: address,
    parsedValue: checksumAddress,
  };

  return parameterState;
}

/**
 * Get the new parameter state for a boolean
 *
 * Used for maintaining the state of a boolean parameter in the transaction builder
 *
 * @param {"true" | "false" | null} bool The boolean string to parse
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForBoolean(
  bool: 'true' | 'false' | true | false | null
): TransactionBuilderParameterState {
  const parsedValue: boolean | undefined = (() => {
    // Case: bool is null
    if (bool === null) return undefined;
    // Case: bool is string representation
    if (bool === 'true') return true;
    if (bool === 'false') return false;
    // Case: bool is just a boolean literal
    return bool;
  })();

  const parameterState: TransactionBuilderParameterState = {
    isValid: bool === 'true' || bool === 'false',
    isEmpty: !bool,
    value: bool !== null ? bool.toString() : '',
    parsedValue,
  };

  return parameterState;
}

/**
 * Get the new parameter state for a bytes parameter
 *
 * Used for maintaining the state of a bytes parameter in the transaction builder
 *
 * @param {string} bytes The bytes string to parse
 * @param {string} solidityType The solidity type of the bytes string (e.g. bytes or bytes32)
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForBytes(
  bytes: string,
  solidityType: string
): TransactionBuilderParameterState {
  const bytesValidationState: BytesValidationState = getBytesValidationState(
    bytes,
    solidityType
  );

  // Update form state
  const parameterState: TransactionBuilderParameterState = {
    isValid:
      bytesValidationState === BytesValidationState.VALID ||
      bytesValidationState === BytesValidationState.VALID_BUT_EMPTY,
    isEmpty: bytesValidationState === BytesValidationState.VALID_BUT_EMPTY,
    value: bytes,
    parsedValue: bytes,
  };

  return parameterState;
}

/**
 * Get the new parameter state for a number parameter
 *
 * Used for maintaining the state of a number parameter in the transaction builder
 *
 * @param {NumberValueType} number The number value type to parse
 * @param {boolean} allowNegative Whether or not to allow negative numbers
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForNumber(
  number: NumberValueType,
  allowNegative: boolean
): TransactionBuilderParameterState {
  // Regex to validate only positive values, and that there are only `number.decimals` characters after the decimal
  const numberValidationState: NumberValidationState = getNumberValidationState(
    number.value,
    number.decimals,
    allowNegative
  );

  let isValid: boolean = numberValidationState === NumberValidationState.VALID;

  const isEmpty: boolean =
    numberValidationState === NumberValidationState.EMPTY;

  const parsedValue: string = (() => {
    // Case: empty value
    if (isEmpty) return '';

    if (number.formatType === 'datetime') {
      // Accept either a datetime string (ISO / locale) OR an integer epoch-seconds string.
      const value = number.value?.trim?.() ?? '';
      const epochSeconds = (() => {
        if (/^[0-9]+$/.test(value)) return Number(value);
        return Math.round(new Date(value).getTime() / 1000);
      })();

      // Case: we are getting a valid integer from the datetime, so it's valid
      if (Number.isFinite(epochSeconds)) {
        isValid = true;
        return epochSeconds.toString();
      }
      return '';
    }

    // Case: invalid number value
    if (!isValid) return '';

    // Case: negative value
    // Parse decimals without negative and add it back in
    if (number.value.startsWith('-'))
      return (
        '-' + parseUnits(number.value.slice(1), number.decimals).toString()
      );

    // Case: positive value
    return parseUnits(number.value || '0', number.decimals).toString();
  })();

  const parameterState: TransactionBuilderParameterState = {
    isValid,
    isEmpty,
    value: JSON.stringify(number),
    parsedValue,
  };

  return parameterState;
}

/**
 * Get the new parameter state for a string parameter
 *
 * Used for maintaining the state of a string parameter in the transaction builder
 *
 * @param {string} str The string to validate
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForString(
  str: string
): TransactionBuilderParameterState {
  const stringValidationState: StringValidationState =
    getStringValidationState(str);

  const parameterState: TransactionBuilderParameterState = {
    isValid: stringValidationState !== StringValidationState.INVALID,
    isEmpty: stringValidationState === StringValidationState.VALID_BUT_EMPTY,
    value: str,
    parsedValue: str,
  };

  return parameterState;
}

/**
 * Get the new parameter state for an array parameter
 *
 * Used for maintaining the state of an array parameter in the transaction builder
 *
 * @param {TransactionBuilderParameterState[]} arrayItems The array items to get the new state for
 * @param {string} solidityType The solidity type of the array (e.g. uint256[] or uint256[2])
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForArray(
  arrayItems: TransactionBuilderParameterState[],
  solidityType: string
): TransactionBuilderParameterState {
  /**
   * Length of array if static, otherwise null
   */
  const staticArraylength: number | null = getStaticArrayLength(solidityType);

  /**
   * Array length is correct
   */
  const isArrayLengthValid: boolean =
    !staticArraylength || arrayItems.length === staticArraylength;

  /**
   * Array is valid
   */
  const isArrayValid: boolean =
    // All items are valid
    isAggregateTypeFullyValidated(arrayItems) &&
    // If static array, length of array is correct
    isArrayLengthValid;

  const parameterState: TransactionBuilderParameterState = {
    value: JSON.stringify(arrayItems),
    isEmpty: arrayItems.length === 0,
    isValid: isArrayValid,
    parsedValue: isArrayValid
      ? getParsedValueForAggregateType(arrayItems)
      : undefined,
  };

  return parameterState;
}

/**
 * Get the new parameter state for a tuple parameter
 *
 * Used for maintaining the state of a tuple parameter in the transaction builder
 *
 * @param {TransactionBuilderParameterState[]} childElements The child elements to get the new parameter state for
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForStructOrTuple(
  childElements: TransactionBuilderParameterState[]
): TransactionBuilderParameterState {
  const isValid: boolean = isAggregateTypeFullyValidated(childElements);
  const parameterState: TransactionBuilderParameterState = {
    value: JSON.stringify(childElements),
    isEmpty: childElements.length === 0,
    isValid,
    parsedValue: isValid
      ? getParsedValueForAggregateType(childElements)
      : undefined,
  };
  return parameterState;
}

/**
 * Get the new parameter state for a generic (i.e. unkown) parameter
 *
 * Used for maintaining the state of a generic parameter in the transaction builder
 *
 * @param {string} input The value of a generic (i.e. unknown) parameter
 * @returns {TransactionBuilderParameterState} The new parameter state
 */
export function getParameterStateForGenericInput(
  input: string
): TransactionBuilderParameterState {
  const parameterState: TransactionBuilderParameterState = {
    isValid: !!input,
    isEmpty: !input,
    value: input,
    parsedValue: input,
  };

  return parameterState;
}

/**
 * Get the validation state of a number parameter
 *
 * @param {string} value The number to validate (in string format)
 * @param {number?} decimals Number of decimals the value can contain
 * @param {boolean?} allowNegative Whether or not to allow negative numbers
 * @returns {NumberValidationState} The validation state of the number
 */
export function getNumberValidationState(
  value: string,
  decimals?: number,
  allowNegative?: boolean
): NumberValidationState {
  // Case: value is empty
  if (value === '') return NumberValidationState.EMPTY;

  // Case: value is negative, but negative values are not allowed
  if (value.startsWith('-') && !allowNegative)
    return NumberValidationState.INVALID_CANT_BE_NEGATIVE;

  // Case: value is allowed to be negative, but is just the negative sign
  if (value === '-') return NumberValidationState.INVALID_CANT_BE_JUST_NEGATIVE;

  // Case: value has incorrect decimals
  if (!isNumberContainingCorrectDecimals(value, decimals)) {
    return NumberValidationState.INVALID_DECIMALS;
  }

  // Case: value ends with decimal
  if (value.endsWith('.')) {
    return NumberValidationState.INVALID_CANT_END_WITH_DECIMAL;
  }

  // Case: value is valid
  return NumberValidationState.VALID;
}

/**
 * Determine if a number value string contains a valid amount of decimals
 *
 * @param {string} value String representing number value inputted by user
 * @param {number?} decimals Number of decimals the value can contain
 * @returns {boolean} `true` if the value contains a valid amount of decimals, `false` otherwise
 */
function isNumberContainingCorrectDecimals(
  value: string,
  decimals?: number
): boolean {
  /**
   * This regex tests that a string representing a *positive* number has a number of decimals equal to
   * the `decimals` parameter
   *
   * E.g. if `decimals` is equal to `3`, it checks that the number
   * is a positive number with 3 decimals, like "7.777"
   *
   * Note: this Regex only tests positive values, so to use it to check negative decimals
   * we first remove the negative sign before testing against this regexp
   *
   * This regex still has some edge cases "e.g. a simple '.' char is considered valid", but
   * doesn't impact user experience and not worth it at this time
   */
  const numberValidationRegex = new RegExp(
    // Note: because regex is a string, we need to escape the backslash with another backlash,
    // so that the regex is actually `^[0-9]*\.?[0-9]{0,${decimals}}$`,
    // with the "." character being escaped in the RegExp
    decimals ? `^[0-9]*\\.?[0-9]{0,${decimals}}$` : '^[0-9]*$',
    'g'
  );
  // Remove negative sign from value if it exists
  const valueWithoutNegative: string = value.replace('-', '');
  return numberValidationRegex.test(valueWithoutNegative);
}

/**
 * Get the validation state of a string parameter
 *
 * @param {string | undefined} str The string to validate
 * @returns {StringValidationState} The validation state of the string
 */
export function getStringValidationState(str?: string): StringValidationState {
  if (str === undefined) return StringValidationState.INVALID;
  if (str === '') return StringValidationState.VALID_BUT_EMPTY;
  return StringValidationState.VALID;
}

/**
 * The current validation state of the string
 *
 * Used to display error and warning messages
 *
 * @param {string} bytesString The bytes string to validate
 * @param {string} bytesSolidityType The solidity type of the bytes as a string (e.g. `bytes32` or `bytes`)
 */
export function getBytesValidationState(
  bytesString: string,
  bytesSolidityType: string
): BytesValidationState {
  // Case: input is not a hexstring (doesn't start with "0x" or contains invalid chars)
  if (!bytesString.startsWith('0x') || !/^0x[0-9a-fA-F]*$/.test(bytesString))
    return BytesValidationState.INVALID_NOT_HEX_STRING;

  // Case: input is a hexstring but isn't correct length of bytes
  const numStaticBytes: number | null =
    getBytesLengthFromType(bytesSolidityType);
  if (numStaticBytes !== null && bytesString.length !== numStaticBytes * 2 + 2)
    return BytesValidationState.INVALID_INCORRECT_LENGTH;

  // Case: input is a hexstring but is not valid bytes (not divisible by 2)
  if (bytesString.length % 2 !== 0)
    return BytesValidationState.INVALID_NOT_BYTES;

  // Case: input is valid and is empty bytes
  if (bytesString === '0x') return BytesValidationState.VALID_BUT_EMPTY;

  // Case: input is valid
  return BytesValidationState.VALID;
}

/**
 * Get the number of bytes the byte string type represents (e.g. `bytes32` returns 32),
 * or null if it's a dynamic `bytes` type
 *
 * @param {string} bytesSolidityType The solidity type of the bytes as a string
 * @returns {number | null} The number of bytes in the bytes type, or null if not specified
 */
export function getBytesLengthFromType(
  bytesSolidityType: string
): number | null {
  const bytesMatch = bytesSolidityType.match(/^bytes([0-9]+)$/);
  // Case: match found
  if (bytesMatch && bytesMatch[1]) {
    return parseInt(bytesMatch[1]);
  }
  // Case: nothing found
  return null;
}

/**
 * RegEx used to detect if a solidity type string is a static array (e.g. uint256[18])
 * and captures its length
 *
 * Do not use this RegEx directly, and instead use helper functions like `getStaticArrayLength`
 *
 * Using this RegEx directly with the `exec` function updates its state
 * and is prone to causing hard to detect bugs
 *
 */
const staticArrayRegEx = /^.+\[([0-9]+)\]$/g;

/**
 * Gets the length of a static array represented by a solidity type string
 *
 * @param {string} solidityType The solidity type of an array as a string
 * @returns {number | null} The length of the array if `solidityType` represents
 *                          a static array, otherwise `null`
 */
export function getStaticArrayLength(solidityType: string): number | null {
  staticArrayRegEx.lastIndex = 0;
  const matches = staticArrayRegEx.exec(solidityType);
  staticArrayRegEx.lastIndex = 0;
  if (!matches) return null;

  const arrayLength = matches[1];
  if (!arrayLength) return null;
  return parseInt(arrayLength);
}

/**
 * RegEx used to detect if a solidity type string is an array
 *
 * Do not use this RegEx directly, and instead use helper functions
 * `getArrayChildType` and `isArrayType`
 *
 * Using this RegEx directly with the `exec` function updates its state
 * and is prone to causing hard to detect bugs
 *
 * This accomodates both:
 *  1. dynamic arrays (e.g. uint26[])
 *  2. static arrays (e.g. uint256[18])
 */
const arrayRegEx = /^(.+)\[[0-9]*\]$/g;

/**
 * Determine if a solidity type string represents and array
 *
 * @param {string} solidityType Solidity type string
 * @returns {boolean} `true` if `solidityType` represents a solidity array
 */
function isArrayType(solidityType: string): boolean {
  return solidityType.match(arrayRegEx) !== null;
}

/**
 * Get the raw type of the children of an array
 * i.e. the type specified before `[]` or `[18] in the solidity type string
 *
 * Uses RegEx capturing group to get the type (note: `.exec` returns array with
 * full match at index 0, with the rest of the elements being capture groups)
 *
 * Resets `arrayRegEx` state before and after `exec` function call for safety
 *
 * @param {string} arraySolidityType Solidity type string of array
 * @returns {string | null} The solidity type string of the array children or null
 */
export function getArrayChildType(arraySolidityType: string): string | null {
  arrayRegEx.lastIndex = 0;
  const result = arrayRegEx.exec(arraySolidityType)?.[1];
  arrayRegEx.lastIndex = 0;
  return result || null;
}

/**
 * Get a higher-abstraction type (ValueType) from a Solidity to use for
 * parsing and displaying tx calldata
 *
 * @param {string} solidityType - A Solidity type as a string
 * @returns {HighLevelSolidityType} - A higher-abstraction type
 */
export function getValueTypeFromSolidityType(
  solidityType: string
): HighLevelSolidityType {
  // Case: array type
  // Note: use RegEx to accomodate for both dynamic and static arrays
  //        (e.g. uint256[] and uint256[18])
  if (isArrayType(solidityType)) return HighLevelSolidityType.ARRAY;

  // Case: struct or tuple type
  if (solidityType.slice(-1) === ')' || solidityType === 'tuple')
    return HighLevelSolidityType.STRUCT_OR_TUPLE;

  // Case: number type
  if (
    // Must be some sort of int type (e.g. uint256 or int8)
    solidityType.includes('int') &&
    // Must not be in a list, tuple, or struct
    !solidityType.includes(',')
  )
    return HighLevelSolidityType.NUMBER;

  // Case: bytes types
  if (solidityType.includes('byte')) return HighLevelSolidityType.BYTES;

  // Case: address type
  if (solidityType.includes('address') || solidityType.includes('payable'))
    return HighLevelSolidityType.ADDRESS;

  // Case: string type
  if (solidityType.includes('string')) return HighLevelSolidityType.STRING;

  // Case: boolean type
  if (solidityType.includes('bool')) return HighLevelSolidityType.BOOLEAN;

  // Case: something else
  return HighLevelSolidityType.PRIMITIVE;
}

/**
 * Get the default field state for a struct or tuple solidity type string
 *
 * @param {AbiParameter} paramAbiFragment - ABI fragment for the struct or tuple
 * @returns {TransactionBuilderParameterState} - The default state for the parameter
 */
function getDefaultFieldStateForStructOrTuple(
  paramAbiFragment: AbiParameter
): TransactionBuilderParameterState {
  // Get the default field states for each element type
  const elementFieldStates: TransactionBuilderParameterState[] =
    (
      paramAbiFragment as AbiParameter & {
        components?: AbiParameter[] | readonly AbiParameter[];
      }
    ).components?.map((componentAbiFragment: AbiParameter) =>
      getDefaultFieldStateForParameterType(componentAbiFragment)
    ) ?? [];

  // Return the struct or tuple field state
  return {
    value: JSON.stringify(elementFieldStates),
    isEmpty: true,
    parsedValue: getParsedValueForAggregateType(elementFieldStates),
    isValid: isAggregateTypeFullyValidated(elementFieldStates),
  };
}

/**
 * Returns the default parameter state object for a parameter of a given solidity type
 *
 * @param {AbiParameter} paramAbiFragment ABI fragment containing the parameter's solidity type
 * @returns {TransactionBuilderParameterState} Default field state for value
 */
export function getDefaultFieldStateForParameterType(
  paramAbiFragment: AbiParameter
): TransactionBuilderParameterState {
  const parameterType: HighLevelSolidityType = getValueTypeFromSolidityType(
    paramAbiFragment.type
  );

  // Case: param is some sort of bytes type
  if (parameterType === HighLevelSolidityType.BYTES)
    return getParameterStateForBytes('0x', paramAbiFragment.type);

  // Case: param is string
  if (parameterType === HighLevelSolidityType.STRING)
    return { value: '', isValid: true, isEmpty: true, parsedValue: '' };

  // Case: param is array
  if (parameterType === HighLevelSolidityType.ARRAY)
    return {
      value: '[]',
      isValid: true,
      isEmpty: true,
      parsedValue: [],
    };

  // Case: param is a struct/tuple
  if (parameterType === HighLevelSolidityType.STRUCT_OR_TUPLE)
    return getDefaultFieldStateForStructOrTuple(paramAbiFragment);

  // Case: param is a number
  if (parameterType === HighLevelSolidityType.NUMBER) {
    const defaultValue: NumberValueType = {
      value: '',
      decimals: 0,
    };
    return {
      value: JSON.stringify(defaultValue),
      isValid: false,
      isEmpty: true,
      parsedValue: '',
    };
  }

  // Case: param is any other type
  return { value: '', isValid: false, isEmpty: true };
}

/**
 * Get the parsed value of an aggregate parameter type (struct, tuple or array), if it's valid, undefined otherwise
 *
 * @param {TransactionBuilderParameterState[]} arrayItems Parameter state objects for all the items in the array
 * @returns {ParameterParsedValue | undefined} The parsed value of the array, or `undefined` if the array
 *                                        is invalid (any item is invalid)
 */
export function getParsedValueForAggregateType(
  arrayItems: TransactionBuilderParameterState[]
): ParameterParsedValue | undefined {
  // Case: any items don't have a parsed value
  if (
    arrayItems.some(
      (item: TransactionBuilderParameterState) => item.parsedValue === undefined
    )
  )
    return undefined;

  // Case: all items have a parsed value
  /**
   * Array of parsed values of each item in the array
   *
   * Note: the type ParameterParsedValue can be a recursive array of `ParameterParsedValue`s,
   *       e.g. `ParameterParsedValue = string | number | boolean | ParameterParsedValue[]`,
   *       so explicit types in the mapping are correct, although may be unintuitive,
   *       as you might expect it to return an array type (which it does, but it's aliased)
   */
  const parsedValue: ParameterParsedValue = arrayItems.map(
    (item: TransactionBuilderParameterState): ParameterParsedValue =>
      item.parsedValue as ParameterParsedValue
  );
  return parsedValue;
}

/**
 * Check if all the items in an aggregate parameter type (struct, tupple, or array) are valid
 *
 * @param {TransactionBuilderParameterState[]} arrayItems Parameter state objects for all the items in the aggregate type
 * @returns {boolean} Whether the aggregate type is valid (all items are valid)
 */
export function isAggregateTypeFullyValidated(
  arrayItems: TransactionBuilderParameterState[]
): boolean {
  return arrayItems.every(
    (item: TransactionBuilderParameterState) => item.isValid
  );
}
